import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const getPatients = () => {
  try {
    return JSON.parse(localStorage.getItem("patients")) || [];
  } catch {
    return [];
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const CustomReport = ({ reset }) => {
  const [clinics, setClinics] = useState([]);
  const [customRange, setCustomRange] = useState(false);

  // Load clinics from localStorage
  useEffect(() => {
    const clinicList = getClinics().filter(c => c.clinicStatus !== "DELETED");
    setClinics(clinicList);
  }, []);

  const formik = useFormik({
    initialValues: {
      clinicId: "",
      Present: "",
      startDate: "",
      endDate: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      clinicId: Yup.string().required("Clinic is required"),
      Present: Yup.string().required("Range is required"),
    }),
    onSubmit: (values) => handleReport(values),
  });

  useEffect(() => {
    setCustomRange(formik.values.Present === 'custom');
  }, [formik.values.Present]);

  // Generate report from localStorage data
  const generateReportData = useCallback((clinicId, startDate, endDate) => {
    const allAppointments = getAppointments();
    const allPatients = getPatients();

    // Filter by clinic
    let filtered = allAppointments;
    if (clinicId && clinicId !== 'all') {
      filtered = filtered.filter(apt => apt.clinicId === parseInt(clinicId, 10));
    }

    // Filter by date range (if provided)
    if (startDate && endDate) {
      filtered = filtered.filter(apt => {
        const aptDate = apt.appointmentDate; // format: YYYY-MM-DD
        return aptDate >= startDate && aptDate <= endDate;
      });
    } else if (startDate) {
      filtered = filtered.filter(apt => apt.appointmentDate >= startDate);
    } else if (endDate) {
      filtered = filtered.filter(apt => apt.appointmentDate <= endDate);
    }

    // Additional grouping/statistics
    const totalPatients = new Set(filtered.map(apt => apt.patientId)).size;
    const totalVisited = filtered.filter(apt => apt.appointmentvisitStatus === "VISITED").length;
    const totalUnpaid = filtered.filter(apt => apt.paymentStatus === "UNPAID").length;
    const totalPaid = filtered.filter(apt => apt.paymentStatus === "PAID").length;
    const totalRevenue = filtered.reduce((sum, apt) => sum + (apt.paymentAmount || 0), 0);

    // Group by clinic if all clinics selected
    let clinicWise = null;
    if (clinicId === 'all') {
      const clinicMap = {};
      filtered.forEach(apt => {
        const key = apt.clinicId;
        if (!clinicMap[key]) {
          clinicMap[key] = { count: 0, visited: 0, unpaid: 0, paid: 0, revenue: 0 };
        }
        clinicMap[key].count++;
        if (apt.appointmentvisitStatus === "VISITED") clinicMap[key].visited++;
        if (apt.paymentStatus === "UNPAID") clinicMap[key].unpaid++;
        if (apt.paymentStatus === "PAID") clinicMap[key].paid++;
        clinicMap[key].revenue += (apt.paymentAmount || 0);
      });
      clinicWise = clinicMap;
    }

    return {
      totalAppointments: filtered.length,
      totalPatients,
      totalVisited,
      totalUnpaid,
      totalPaid,
      totalRevenue,
      clinicWise,
      appointments: filtered, // full list for detailed view
    };
  }, []);

  const handleReport = useCallback((values) => {
    let startDate = values.startDate;
    let endDate = values.endDate;

    // Auto-set dates based on range type
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    if (values.Present === 'month') {
      startDate = `${year}-${month}-01`;
      endDate = todayStr;
    } else if (values.Present === 'year') {
      startDate = `${year}-01-01`;
      endDate = todayStr;
    }

    // Generate report data
    const report = generateReportData(values.clinicId, startDate, endDate);

    // Show report summary (you can replace with download or display)
    console.log("📊 Custom Report:", report);

    toast.success(
      `Report generated: ${report.totalAppointments} appointments, ₹${report.totalRevenue} revenue`,
      { autoClose: 5000 }
    );

    // Optionally, you can display a detailed modal or download as CSV/PDF
    // For now, we just log and toast.

    reset(); // close modal
  }, [generateReportData, reset]);

  const clinicOptions = useMemo(() => {
    return clinics.map(clinic => (
      <option key={clinic.id} value={clinic.id} data-location={clinic.location}>
        {clinic.location}
      </option>
    ));
  }, [clinics]);

  return (
    <div
      id="login-popup"
      tabIndex="-1"
      className="bg-black/50 fixed top-0 right-0 left-0 z-50 h-full flex items-center justify-center"
    >
      <div className="relative p-4 w-full max-w-xl">
        <div className="relative bg-white rounded-lg shadow">
          <button
            type="button"
            className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5"
            onClick={reset}
          >
            <svg
              aria-hidden="true"
              className="w-5 h-5"
              fill="#c6c7c7"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <div className="py-6 px-6 lg:px-8">
            <h3 className="mb-4 text-xl font-medium text-gray-900">
              Create Custom Report
            </h3>
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              {/* Clinic Selection */}
              <div>
                <label
                  htmlFor="clinics"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Select Clinic
                </label>
                <select
                  name="clinicId"
                  id="clinics"
                  className="border text-sm rounded-lg block w-full p-2.5"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.clinicId}
                >
                  <option value="">Select</option>
                  <option value="all">All Clinics</option>
                  {clinicOptions}
                </select>
                {formik.touched.clinicId && formik.errors.clinicId && (
                  <p className="text-red-700 mt-2 text-xs">
                    {formik.errors.clinicId}
                  </p>
                )}
              </div>
              {/* Report Range Selection */}
              <div>
                <label
                  htmlFor="Present"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Select Report Range
                </label>
                <select
                  name="Present"
                  id="Present"
                  className="border text-sm rounded-lg block w-full p-2.5"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Present}
                >
                  <option value="">Select</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>
                {formik.touched.Present && formik.errors.Present && (
                  <p className="text-red-700 mt-2 text-xs">
                    {formik.errors.Present}
                  </p>
                )}
              </div>
              {/* Date Range Inputs */}
              {customRange && (
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { name: "startDate", label: "Start Date", value: formik.values.startDate },
                    { name: "endDate", label: "End Date", value: formik.values.endDate }
                  ].map(({ name, label, value }) => (
                    <div key={name} className="col-span-2 sm:col-span-1">
                      <label
                        htmlFor={name}
                        className="block mb-2 text-sm font-medium text-gray-900"
                      >
                        {label}
                      </label>
                      <input
                        type="date"
                        name={name}
                        id={name}
                        className="border text-sm rounded-lg block w-full p-2.5"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={value}
                      />
                    </div>
                  ))}
                </div>
              )}
              {/* Submit Button */}
              <button
                type="submit"
                className="w-full text-white bg-red-500 hover:bg-red-600 rounded-lg text-sm px-5 py-2.5"
              >
                Generate Report
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReport;